/**
 * Health Recommendations Page
 * Displays personalized health advice for travel destinations
 */

import { DiseaseOutbreakCard } from "@/components/health/DiseaseOutbreakCard";
import { HealthAuthorityBadge } from "@/components/health/HealthAuthorityBadge";
import { HealthRecommendationCard } from "@/components/health/HealthRecommendationCard";
import { VaccinationCard } from "@/components/health/VaccinationCard";
import {
  getDestinationHealthRecommendations,
  type AggregatedHealthAdvice,
} from "@/services/api/healthRecommendations";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useI18nStore } from "@/stores/i18nStore";
import { i18nToast } from "@/utils/i18nToast";
import {
  AlertTriangle,
  Calendar,
  Globe,
  Info,
  Shield,
  Syringe,
  TrendingUp,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const HealthRecommendationsPage: React.FC = () => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const navigate = useNavigate();
  const t = useI18nStore((state) => state.t);
  const fmt = useLocaleFormat();

  const riskLabels: Record<string, string> = {
    low: t.recsRiskLow,
    moderate: t.recsRiskModerate,
    high: t.recsRiskHigh,
    critical: t.recsRiskCritical,
  };
  const travelTypeLabels: Record<string, string> = {
    cruise: t.recsTravelCruise,
    flight: t.recsTravelFlight,
    land: t.recsTravelLand,
    mixed: t.recsTravelMixed,
  };

  const [loading, setLoading] = useState(true);
  const [healthAdvice, setHealthAdvice] =
    useState<AggregatedHealthAdvice | null>(null);
  const departureDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    if (countryCode) {
      loadHealthRecommendations();
    }
  }, [countryCode, departureDate]);

  async function loadHealthRecommendations() {
    if (!countryCode) return;

    try {
      setLoading(true);
      const data = await getDestinationHealthRecommendations(countryCode, {
        ...(departureDate && { departureDate }),
        travelType: "cruise" as const,
      });
      setHealthAdvice(data);
    } catch (error: any) {
      console.error("Failed to load health recommendations:", error);
      i18nToast.error(t.failedToLoadHealthRecommendations);
    } finally {
      setLoading(false);
    }
  }

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case "critical":
        return "text-red-600 bg-red-50 border-red-200";
      case "high":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "moderate":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "low":
        return "text-green-600 bg-green-50 border-green-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getRiskLevelIcon = (level: string) => {
    switch (level) {
      case "critical":
        return <AlertTriangle className="h-6 w-6" />;
      case "high":
        return <AlertTriangle className="h-6 w-6" />;
      case "moderate":
        return <Info className="h-6 w-6" />;
      case "low":
        return <Shield className="h-6 w-6" />;
      default:
        return <Info className="h-6 w-6" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!healthAdvice) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t.noHealthDataAvailable}
          </h3>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
          >
            {t.backToDashboard}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="text-teal-600 hover:text-teal-700 mb-4 flex items-center"
        >
          ← {t.back}
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t.healthRecommendations}
            </h1>
            <p className="mt-2 text-gray-600">
              {t.destination}: {healthAdvice.profile.destinationCountry}
            </p>
          </div>

          {/* Risk Level Badge */}
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getRiskLevelColor(healthAdvice.riskLevel)}`}
          >
            {getRiskLevelIcon(healthAdvice.riskLevel)}
            <div>
              <div className="text-xs font-medium">{t.riskLevel}</div>
              <div className="text-sm font-bold">
                {riskLabels[healthAdvice.riskLevel] ?? healthAdvice.riskLevel}
              </div>
            </div>
          </div>
        </div>

        {/* Travel Details */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>
              {t.departure}: {fmt.date(healthAdvice.profile.departureDate)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Globe className="h-4 w-4" />
            <span>
              {t.travelType}:{" "}
              {travelTypeLabels[healthAdvice.profile.travelType] ??
                healthAdvice.profile.travelType}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <TrendingUp className="h-4 w-4" />
            <span>
              {t.lastUpdated}: {fmt.dateTime(healthAdvice.lastUpdated)}
            </span>
          </div>
        </div>
      </div>

      {/* Required Vaccinations */}
      {healthAdvice.requiredVaccinations.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Syringe className="h-5 w-5 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t.requiredVaccinations}
            </h2>
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
              {t.mandatory}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {healthAdvice.requiredVaccinations.map((vaccination, index) => (
              <VaccinationCard
                key={index}
                vaccination={vaccination}
                isRequired
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommended Vaccinations */}
      {healthAdvice.recommendedVaccinations.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Syringe className="h-5 w-5 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t.recommendedVaccinations}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {healthAdvice.recommendedVaccinations.map((vaccination, index) => (
              <VaccinationCard key={index} vaccination={vaccination} />
            ))}
          </div>
        </div>
      )}

      {/* Active Disease Outbreaks */}
      {healthAdvice.activeOutbreaks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t.activeDiseaseOutbreaks}
            </h2>
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
              {healthAdvice.activeOutbreaks.length}
            </span>
          </div>
          <div className="space-y-4">
            {healthAdvice.activeOutbreaks.map((outbreak) => (
              <DiseaseOutbreakCard key={outbreak.id} outbreak={outbreak} />
            ))}
          </div>
        </div>
      )}

      {/* Health Recommendations */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-teal-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            {t.healthRecommendations}
          </h2>
          <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs font-medium rounded">
            {healthAdvice.recommendations.length}
          </span>
        </div>
        <div className="space-y-4">
          {healthAdvice.recommendations.map((recommendation) => (
            <HealthRecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
            />
          ))}
        </div>
      </div>

      {/* Data Sources */}
      <div className="border-t pt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          {t.dataSources}
        </h3>
        <div className="flex flex-wrap gap-2">
          {healthAdvice.sources.map((source) => (
            <HealthAuthorityBadge key={source.id} authority={source} />
          ))}
        </div>
      </div>
    </div>
  );
};
